import { HttpErrorResponse, type HttpInterceptorFn, type HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { APP_CONFIG } from '../config/app-config';
import { isThirdPartyUrl } from '../http/absolute-url';
import { SKIP_AUTH } from './auth.context';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const tokens = inject(TokenStorageService);
  const auth = inject(AuthService);
  const { platform, apiUrl } = inject(APP_CONFIG);

  if (isThirdPartyUrl(request.url, apiUrl)) {
    return next(request);
  }

  const authorized = withSessionHeaders(request, tokens.accessToken, tokens.deviceId(), platform);

  return next(authorized).pipe(
    catchError((error: unknown) => {
      if (!request.context.get(SKIP_AUTH) && isSessionRejected(error)) {
        auth.endSession('expired');
      }

      return throwError(() => error);
    }),
  );
};

function isSessionRejected(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  if (error.status === 401) {
    return true;
  }

  return error.status === 400 && errorCodeOf(error) === 'AUTH_BAD_REQUEST';
}

function errorCodeOf(error: HttpErrorResponse): string | null {
  const body: unknown = error.error;

  if (typeof body !== 'object' || body === null || !('errors' in body)) {
    return null;
  }

  const { errors } = body as { errors?: unknown };

  if (!Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  const first: unknown = errors[0];

  if (typeof first !== 'object' || first === null || !('error' in first)) {
    return null;
  }

  const code = (first as { error?: unknown }).error;

  return typeof code === 'string' ? code : null;
}

function withSessionHeaders<T>(
  request: HttpRequest<T>,
  token: string | null,
  deviceId: string,
  platform: string,
): HttpRequest<T> {
  const headers: Record<string, string> = {
    'X-Correlation-ID': deviceId,
    'X-Request-ID': 'aaa',
    Platform: platform,
  };

  if (token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return request.clone({ setHeaders: headers });
}
